import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Body1, Caption1, EmptySlate } from "@activecollab/components";
import classNames from "classnames";
import {
  STATUS_GLOBAL_DAY_OFF,
  STATUS_NON_WORKING_DAYS,
  STATUS_NOT_AVAILABLE,
  STATUS_USER_DAY_OFF,
} from "~/components/Capacity";
import { CapacityLoader } from "~/components/Capacity/CapacityLoader";
import { UsersColumn } from "~/components/Capacity/UsersColumn";
import { UtilizationLegend } from "~/components/Capacity/UtilizationLegend";
import { getProgressColor, UtilizationPie } from "~/components/Capacity/UtilizationPie";
import { useUtilization } from "~/repository";
import { getPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getUsersByIds } from "~/store/selectors/selector.users";
import { minutesToHours } from "~/utils/utils";

const handleNoUtilization = (status) => {
  return status === STATUS_NOT_AVAILABLE || status === STATUS_NON_WORKING_DAYS || status === STATUS_GLOBAL_DAY_OFF;
};

export const UtilizationTable = () => {
  const { t } = useTranslation();
  const capacityFilterObject = useSelector((state) => getPersonalizedConfigOption(state, "capacity_filter")).toJS();
  const { from, to, setRouteQueryState } = useOutletContext();

  const { data, isFetching, isError } = useUtilization({
    from,
    to,
    scale: capacityFilterObject?.scale ?? "weekly",
    user_ids: capacityFilterObject?.users ?? [],
    show_only_billable: true, // @todo config option
  });

  const { users_data = [], columns = [], current_period, total_utilization } = data || {};

  const userIds = users_data.map((user) => user.user_id);
  const users = useSelector((state) => getUsersByIds(state, userIds));

  useEffect(() => {
    setRouteQueryState({
      isFetching,
      isError: data ? isError : false,
      hasUsers: users_data.length > 0,
    });
  }, [data, isError, isFetching, setRouteQueryState, users_data.length]);

  const sortedUsers = [...users_data].sort((a, b) => {
    const aName = users.get(`${a.user_id}`)?.get("display_name")?.toLowerCase() ?? "";
    const bName = users.get(`${b.user_id}`)?.get("display_name")?.toLowerCase() ?? "";

    return aName.localeCompare(bName);
  });

  if (isFetching) {
    return <CapacityLoader />;
  }

  const hasData = users_data.length > 0 && (data ? !isError : true);

  return hasData ? (
    <div>
      <div className="tw-flex tw-w-full tw-min-w-0">
        <UsersColumn
          usersData={sortedUsers}
          context="utilization-user-link"
          isFetching={isFetching}
        />

        <div className="tw-min-w-0 tw-overflow-x-auto tw-flex-grow">
          <div className="tw-min-w-max">
            <div className="tw-flex">
              {columns.map((col) => (
                <div
                  key={col.key}
                  className="tw-flex-[1_0_104px] tw-flex tw-flex-col [&:last-child>div:last-child]:tw-border-r-0"
                >
                  <Caption1
                    className={classNames(
                      {
                        "capacity-current-period-column tw-bg-primary-300": col.key === current_period,
                      },
                      "tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
                    )}
                    weight="bold"
                    color="secondary"
                  >
                    {col.label}
                  </Caption1>
                  <Body1
                    style={{
                      backgroundColor: col.disabled
                        ? "var(--color-primary-200)"
                        : getProgressColor(col.total_utilization, true),
                    }}
                    weight="bold"
                    className="tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
                  >
                    {col.disabled ? "-" : `${col.total_utilization} %`}
                  </Body1>
                </div>
              ))}
            </div>

            {sortedUsers.map((user, index) => (
              <div
                key={user.user_id}
                className="tw-flex"
              >
                {columns.map((col) => {
                  const cell = user.columns_data[col.key];

                  return cell ? (
                    <Body1
                      key={col.key}
                      weight="bold"
                      color={cell.capacity > 0 && cell.status !== STATUS_USER_DAY_OFF ? "primary" : "alert"}
                      className={classNames(
                        {
                          "tw-bg-theme-200": index % 2 === 0,
                          "capacity-disabled-day": handleNoUtilization(cell.status),
                        },
                        "tw-h-10 tw-flex-[1_0_104px] tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0 last:tw-border-r-0"
                      )}
                    >
                      {handleNoUtilization(cell.status) ? null : (
                        <>
                          {cell.status === STATUS_USER_DAY_OFF && cell.tracked_time === 0 ? (
                            "OFF"
                          ) : (
                            <>
                              {minutesToHours(cell.tracked_time, true)}/{minutesToHours(cell.capacity, true)}{" "}
                              <UtilizationPie
                                utilization={cell.utilization}
                                className="tw-ml-2"
                              />{" "}
                            </>
                          )}
                        </>
                      )}
                    </Body1>
                  ) : null;
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="tw-w-[104px] tw-shrink-0">
          <Caption1
            weight="bold"
            color="secondary"
            className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
          >
            {t("Total")}
          </Caption1>

          <Body1
            weight="bold"
            className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0"
            style={{
              backgroundColor: getProgressColor(total_utilization, true),
            }}
          >
            {total_utilization} %
          </Body1>

          {sortedUsers.map((user) => (
            <Body1
              key={user.user_id}
              weight="bold"
              className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0"
              style={{
                backgroundColor: getProgressColor(user.total_utilization, true),
              }}
            >
              {user.total_utilization} %
            </Body1>
          ))}
        </div>
      </div>
      <UtilizationLegend className="tw-justify-end tw-my-4 tw-mr-4" />
    </div>
  ) : (
    <EmptySlate
      title={t("No Data")}
      src="/assets/environment/images/layout/search_empty_page.png"
      alt="Utilization"
      description={t("Adjust your filters and run the report again.")}
      className="tw-my-auto"
    />
  );
};
